import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, ClipboardCheck, Truck, ListChecks, Users, UserCircle2, Star, GraduationCap, ArrowRight } from "lucide-react";
import { Panel } from "@/components/admin/ui";

function Card({ icon: Icon, label, value, sub, to, tint = "bg-neutral-100 text-neutral-700", testId }) {
  return (
    <Link to={to} className="block" data-testid={testId}>
      <Panel className="p-5 h-full hover:shadow-md hover:-translate-y-0.5 transition-all">
        <div className={`w-10 h-10 rounded-xl ${tint} flex items-center justify-center mb-4`}><Icon className="w-5 h-5"/></div>
        <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
        <div className="font-display font-bold text-3xl mt-1">{value}</div>
        {sub && <div className="text-sm text-neutral-500 mt-1">{sub}</div>}
      </Panel>
    </Link>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/dashboard/summary").then((r) => setS(r.data)).catch(() => setS(false)); }, []);
  if (s === null) return <div className="p-10 text-neutral-500">Loading dashboard…</div>;
  if (s === false) return <div className="p-10 text-neutral-500">Could not load dashboard.</div>;

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-8">
        <p className="eyebrow mb-2">All Stores</p>
        <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="dashboard-title">Sandar Sushi Dashboard</h1>
        <p className="text-neutral-500 mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4"/>{s.locations} Location{s.locations !== 1 ? "s" : ""}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <Card icon={ClipboardCheck} label="Inventory" value={`${s.inventory_updated_today}/${s.locations}`} sub="stores counted today" to="/admin/inventory-counts" tint="bg-emerald-100 text-emerald-700" testId="dash-inventory"/>
        <Card icon={Truck} label="Restock Requests" value={s.restock_new} sub={`new · ${s.restock_open} open`} to="/admin/restock-requests" tint="bg-amber-100 text-amber-700" testId="dash-restock"/>
        <Card icon={ListChecks} label="Tasks" value={s.tasks_open} sub={`open · ${s.tasks_overdue} overdue`} to="/admin/stores" tint="bg-blue-100 text-blue-700" testId="dash-tasks"/>
        <Card icon={Users} label="Employees" value={s.employees} sub="team members" to="/admin/employees" tint="bg-neutral-100 text-neutral-700" testId="dash-employees"/>
        <Card icon={UserCircle2} label="Customers" value={s.customers.toLocaleString()} sub="registered" to="/admin/customers" tint="bg-purple-100 text-purple-700" testId="dash-customers"/>
        <Card icon={Star} label="Feedback" value={s.feedback_avg != null ? `${s.feedback_avg} ★` : "—"} sub={`${s.feedback_count} reviews`} to="/admin/feedback" tint="bg-yellow-100 text-yellow-700" testId="dash-feedback"/>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-4">
        <Panel className="overflow-hidden">
          <div className="p-4 border-b border-neutral-100 flex items-center justify-between"><h2 className="font-display font-semibold">Locations</h2><Link to="/admin/stores" className="text-xs text-[hsl(var(--primary))] font-medium">Manage →</Link></div>
          <div className="divide-y divide-neutral-100">
            {s.stores.map((st) => (
              <Link key={st.id} to={`/admin/stores/${st.id}`} className="flex items-center gap-4 p-4 hover:bg-neutral-50 text-sm" data-testid={`dash-store-${st.id}`}>
                <div className="flex-1 font-medium">{st.name}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${st.counted_today ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>{st.counted_today ? "Counted" : "No count"}</span>
                <span className="text-xs text-neutral-500 w-24 text-right">{st.restock_new} restock</span>
                <span className="text-xs text-neutral-500 w-20 text-right">{st.tasks_open} tasks</span>
                <ArrowRight className="w-4 h-4 text-neutral-300"/>
              </Link>
            ))}
          </div>
        </Panel>
        <Link to="/admin/training" data-testid="dash-training">
          <div className="rounded-2xl p-5 h-full bg-neutral-950 text-white hover:-translate-y-0.5 transition-transform">
            <GraduationCap className="w-8 h-8 mb-4 text-[hsl(var(--primary))]"/>
            <div className="text-xs uppercase tracking-wider text-neutral-400">Training & SOP</div>
            <div className="font-display font-bold text-3xl mt-1">{s.training_lessons}</div>
            <div className="text-sm text-neutral-400 mt-1">published lessons</div>
          </div>
        </Link>
      </div>
    </div>
  );
}
